import type { SelectableParty } from '@clm/shared-types';

/**
 * The party nearest-match fallback's suggestion threshold — CLM-126.
 *
 * Mirrors `apps/api/src/data/party-match-queries.ts`'s `PARTY_MATCH_SUGGEST_FLOOR`
 * and `PARTY_MATCH_NEAR_TIE` (this app takes no dependency on that module, same
 * precedent as `./ingest/sow-shape.ts` mirroring `classifySowCandidate`): the
 * two numbers are pinned equal by `party-match.test.ts`. Kept out of the
 * component files so Fast Refresh sees only components there.
 *
 * The candidates themselves (`BundlePlanRow.nearest_matches` /
 * `SkippedDocument.nearest_matches`) are never re-scored here — the server is
 * the only place `similarity()` runs (rule 14). This module only decides,
 * from scores already sent, whether the leading one is worth a one-click
 * "Suggested:" callout (R-126-6) or merely an ordinary candidate the picker's
 * `initialParties` offers with no callout (below the floor, or inside the
 * near-tie band against the runner-up — R-126-7).
 */

/** At or above this score, and clear of the near-tie band, a candidate is offered as a named suggestion. */
export const PARTY_MATCH_SUGGEST_FLOOR = 0.6;

/** Scores within this of each other are a coin flip — neither is singled out as "the" suggestion. */
export const PARTY_MATCH_NEAR_TIE = 0.05;

/**
 * The one candidate to offer as a one-click "Use …" suggestion, or `null` when
 * none qualifies — no leading candidate, no score, below the floor, or too
 * close to the runner-up to single out (R-126-7). `candidates` is already in
 * the server's own deterministic order (score descending, name ascending on a
 * tie — R-126-8), so only the first two matter here.
 */
export function leadingSuggestion(candidates: readonly SelectableParty[]): SelectableParty | null {
  const leader = candidates[0];
  if (leader === undefined) return null;
  const leaderScore = leader.suggestion_score;
  if (leaderScore === null || leaderScore === undefined || leaderScore < PARTY_MATCH_SUGGEST_FLOOR) return null;
  const runnerUp = candidates[1];
  const runnerUpScore = runnerUp?.suggestion_score;
  if (runnerUpScore !== null && runnerUpScore !== undefined && leaderScore - runnerUpScore < PARTY_MATCH_NEAR_TIE) {
    return null;
  }
  return leader;
}

/** "~62% match" — the same rounding `PartyPicker` uses for `suggestion_score`. */
export function matchPercent(score: number): string {
  return `~${String(Math.round(score * 100))}% match`;
}
